import React, { useState } from "react";
import Countdown from "react-countdown";
import TimeAgo from "javascript-time-ago";
import { Link, useNavigate } from "react-router-dom";
import Button from "./Button";
import LevelBadge from "./LevelBadge";
import {
  ItemBonusStat,
  ItemBonusStatIcons,
  ItemSlotNames,
  ItemStateEnum,
  ItemStateType,
  PlayerItemType,
} from "../types/items";
import Skeleton from "react-loading-skeleton";
import { Badge } from "react-bootstrap";
import { ClegIcon, CustomIcon } from "./Icons";
import { roundFloat } from "../utils/round-float";
import ThousandsSeparator from "../utils/thousands-separator";
import useWindowSize from "../hooks/useWindowSize";
import { useAppTranslation } from "../i18n/useAppTranslation";
import AlteredLabel from "./AlteredLabel";
import ModifiedStat from "./ModifiedStat";
import { itemOriginalBonus } from "../utils/statModifiers";

type Props = {
  loading?: boolean;
  data?: PlayerItemType;
  isMarket?: boolean;
  Ask?: number;
  Bid?: number;
  onSell?: (item: PlayerItemType) => void;
  onMerge?: (item: PlayerItemType) => void;
  onUnuse?: (item: PlayerItemType) => void;
  onCancel?: (item: PlayerItemType) => void;
  refreshList?: () => void;
  noAction?: boolean;
};

const ItemCard = ({
  data,
  refreshList,
  loading,
  onSell,
  onMerge,
  onCancel,
  isMarket,
  Ask,
  Bid,
  onUnuse,
  noAction,
}: Props) => {
  const { t } = useAppTranslation(["barracks", "common"]);
  const navigate = useNavigate();
  const getItemStateLabel = (state?: number) => {
    switch (state) {
      case ItemStateEnum.Ready:
        return t("barracks:states.ready");
      case ItemStateEnum.InUse:
        return t("barracks:itemsTab.states.inUse");
      case ItemStateEnum.Merged:
        return t("barracks:states.merged");
      case ItemStateEnum.ForSale:
        return t("barracks:states.forSale");
      default:
        return t(`barracks:states.${ItemStateType[state || 0]}`, {
          defaultValue: "",
        });
    }
  };
  const isForSale = React.useMemo(
    () => data?.state === ItemStateEnum.ForSale,
    [data]
  );
  const isInUse = React.useMemo(
    () => data?.state === ItemStateEnum.InUse,
    [data]
  );
  const windowSize = useWindowSize();
  // While an event modifier changes this item, bonusValue is the modified bonus.
  const originalBonus = itemOriginalBonus(data);
  if (data && !loading)
    return (
      <div
        onClick={() =>
          isMarket &&
          navigate(
            `/marketplace/orderbook/item?itemSlots=${data?.item.slot}&levels=${data?.level}&itemStats=${data?.item.bonusStat}&itemId=${data?.itemId}`
          )
        }
        className={"adventure-card " + (isMarket && "pointer")}
      >
        <AlteredLabel
          theme={data.statModifiers?.theme}
          kind="item"
          placement="top"
        />
        <div className="d-flex">
          <div className="adventure-card-content">
            <div className="d-flex gap-2 align-items-center ">
              <div className="p-relative">
                <img
                  style={{
                    maxWidth: windowSize.width < 700 ? 60 : 99,
                    maxHeight: 150,
                  }}
                  src={data.image}
                />

                {!isMarket && (isInUse || isForSale) && (
                  <Badge
                    className="item-badge"
                    bg={isInUse ? "warning" : "danger"}
                  >
                    {getItemStateLabel(data.state)}
                  </Badge>
                )}
              </div>

              <div className="d-flex w-100 gap-1 flex-column ">
                <div className="d-flex w-100 align-items-center justify-content-between">
                  <div className="fs-med">{data.item?.name}</div>
                  <LevelBadge
                    style={{
                      position: "relative",
                      zIndex: 10,
                      marginInline: 0,
                    }}
                    level={data.level}
                  />
                </div>
                <div
                  style={{ maxWidth: "170px" }}
                  className="fs-small text-truncate"
                >
                  <img
                    src={ItemBonusStatIcons[data.item.bonusStat]}
                    width={20}
                    height={24}
                    className="me-1"
                    alt={t(`barracks:stats.${ItemBonusStat[data.item.bonusStat]}`)}
                  />
                  +
                  <ModifiedStat
                    value={roundFloat(data.bonusValue!)}
                    original={
                      originalBonus === undefined
                        ? undefined
                        : roundFloat(originalBonus)
                    }
                  />{" "}
                  {data.itemId === 18
                    ? t("barracks:itemsTab.magicDamage")
                    : t(`barracks:stats.${ItemBonusStat[data.item.bonusStat]}`)}
                </div>
                {!isMarket && isForSale && (
                  <div className="fs-small mt-2 d-flex align-items-center">
                    <ClegIcon width={20} height={20} />{" "}
                    {ThousandsSeparator(roundFloat(data.sellOrder?.clegPrice!))}{" "}
                    {t("barracks:stats.cleg")}
                    <Button
                      onClick={() => onCancel && onCancel(data)}
                      size="small"
                      className="ms-2"
                      variant="default"
                    >
                      {t("common:actions.cancel")}
                    </Button>
                  </div>
                )}
                <div>
                  {!isMarket && !isForSale && !noAction && (
                    <div className="d-flex mt-1  gap-2 align-items-center">
                      {data.state === ItemStateEnum.InUse ? (
                        <Button
                          onClick={() => onUnuse && onUnuse(data)}
                          size="small"
                          className="px-2"
                          variant="default"
                        >
                          <img
                            height={15}
                            width={14}
                            className="me-1"
                            src="/assets/images/icons/remove.png"
                          />
                          {t("barracks:itemsTab.detach")}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => onSell && onSell(data)}
                          size="small"
                          variant="default"
                        >
                          <img
                            width={15}
                            className="me-1"
                            src="/assets/images/icons/offer.png"
                          />
                          {t("common:actions.sell")}
                        </Button>
                      )}

                      <Button
                        onClick={() => onMerge && onMerge(data)}
                        size="small"
                        variant="primary"
                      >
                        <img
                          width={15}
                          className="me-1"
                          src="/assets/images/icons/merge.png"
                        />
                        {t("common:actions.merge")}
                      </Button>
                    </div>
                  )}
                </div>
                {isMarket && (
                  <div className="fs-small">
                    <div>
                      {t("barracks:stats.buyerPrice")}:{" "}
                      <span className="text-success bold">
                        {ThousandsSeparator(Bid?.toString() || "0") || 0}{" "}
                        <small className="dark-gray">
                          <ClegIcon width={20} height={20} /> {t("barracks:stats.cleg")}
                        </small>
                      </span>
                    </div>

                    <div>
                      {t("barracks:stats.sellerPrice")}:{" "}
                      <span className="text-danger bold">
                        {ThousandsSeparator(Ask?.toString() || "0")}{" "}
                        <small className="dark-gray">
                          <ClegIcon width={20} height={20} /> {t("barracks:stats.cleg")}
                        </small>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="adventure-card">
      <div className="d-flex">
        <div className="adventure-card-content">
          <div className="d-flex gap-2 ">
            <Skeleton width={90} height={90} circle />
            <div className="d-flex w-100 flex-column ">
              <div className="d-flex w-100 align-items-center justify-content-between">
                <div className="fs-lg">
                  <Skeleton width={111} />
                </div>
              </div>
              <div className="fs-med">
                <Skeleton width={50} count={2} />
              </div>
              <div>
                <div className="d-flex mt-2 gap-2 align-items-center">
                  <Skeleton width={50} />
                  <Skeleton width={50} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
